import { useCallback, useEffect, useState } from 'react';

/**
 * The cog entries for the audit pair, and the reader's say over them.
 *
 * `Created` and `Latest update` are appended to every index table and every panel table by the
 * shell, from the `otherInfo` each surface already supplies — so no page declares them and no
 * page could put them away. Two columns at 220px each is 440px of provenance on a table whose
 * reader came for the record, and on most screens it is the first thing scrolled past.
 *
 * **They default to HIDDEN** (product call, 27 Oct 2026). The fact is not lost: `Other Info`
 * carries the whole provenance on every row already, which is what makes a default-off column
 * affordable here and would not make one affordable anywhere else.
 *
 * **It is its own store rather than a group in `useHeaderGroups`**, and the reason is the
 * default. That hook persists what the reader has put AWAY, so an absent value and an empty
 * array both mean "everything is showing" — a set that cannot express "hidden until somebody
 * asks". This one persists what the reader has turned ON, so absent means hidden, and nothing
 * has to be seeded into storage at first paint to make the default true.
 *
 * **The TABLE only.** The list view's `Latest update` stamp is part of the row, not a column,
 * and the cog section these entries sit in is headed `Headers`.
 */
export const AUDIT_COLUMN_TOGGLES: { key: string; label: string }[] = [
  { key: 'createdAt', label: 'Created' },
  { key: 'updatedAt', label: 'Latest update' },
];

/** The cog group key for an audit column, namespaced so a page's own group cannot collide. */
export function auditGroupKey(columnKey: string): string {
  return `audit:${columnKey}`;
}

/** The column key behind a cog group key, or null where the group is the page's own. */
export function auditColumnKey(groupKey: string): string | null {
  return groupKey.startsWith('audit:') ? groupKey.slice('audit:'.length) : null;
}

function storageKey(key: string): string {
  return `index.${key}.auditColumns`;
}

function read(key: string): string[] {
  try {
    const stored = localStorage.getItem(storageKey(key));
    if (stored === null) return [];

    const parsed: unknown = JSON.parse(stored);

    return Array.isArray(parsed)
      ? parsed.filter((value): value is string => typeof value === 'string')
      : [];
  } catch {
    // Blocked storage, or a value written by an older shape. Falling back to the DEFAULT is
    // the same answer a first visit gets, so a reader whose site data cannot be read sees the
    // table everybody else sees rather than one with two columns they never asked for.
    return [];
  }
}

/**
 * Which of the audit pair this surface draws, persisted per module — `shown`, not hidden.
 *
 * The key is the module (or the panel id), the same key the column widths and the view type
 * are stored under, so one reader's arrangement of one table travels together.
 */
export function useAuditColumnVisibility(key: string) {
  const [shown, setShown] = useState<string[]>(() => read(key));

  useEffect(() => {
    try {
      localStorage.setItem(storageKey(key), JSON.stringify(shown));
    } catch {
      // Persisting the layout is a convenience, never a requirement.
    }
  }, [key, shown]);

  const toggle = useCallback((columnKey: string) => {
    setShown((current) =>
      current.includes(columnKey)
        ? current.filter((k) => k !== columnKey)
        : [...current, columnKey],
    );
  }, []);

  const isShown = useCallback((columnKey: string) => shown.includes(columnKey), [shown]);

  return { shown, toggle, isShown };
}
